import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import api from "../lib/api";

// Verdict → color for the pill inside each ticker item
const V_COLOR = {
    "true":         "#22e0b4",
    "mostly_true":  "#7cf5c8",
    "misleading":   "#f5b942",
    "false":        "#ff6b6b",
    "unverified":   "#8890c9",
    "safe":         "#22e0b4",
    "likely_safe":  "#7cf5c8",
    "suspicious":   "#f5b942",
    "scam":         "#ff6b6b",
};

const V_LABEL = {
    "true": "TRUE", "mostly_true": "MOSTLY TRUE", "misleading": "MISLEADING",
    "false": "FALSE", "unverified": "UNVERIFIED",
    "safe": "SAFE", "likely_safe": "LIKELY SAFE",
    "suspicious": "SUSPICIOUS", "scam": "SCAM",
};

function ago(iso) {
    if (!iso) return "";
    const t = Date.parse(iso);
    if (isNaN(t)) return "";
    const secs = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
}

/**
 * Live Fact-Check Ticker
 * Sleek newsroom-style scrolling bar. Sits under the site header.
 * Pulls /api/trending every 30s. Pauses on hover.
 * Uses two duplicated tracks + CSS transform for seamless loop.
 */
export default function LiveTicker() {
    const [items, setItems] = useState([]);
    const [paused, setPaused] = useState(false);
    const trackRef = useRef(null);

    useEffect(() => {
        let alive = true;
        const load = () =>
            api.get("/trending").then((r) => {
                if (!alive) return;
                setItems((r.data || []).slice(0, 30));
            }).catch(() => {});
        load();
        const t = setInterval(load, 30_000);
        return () => { alive = false; clearInterval(t); };
    }, []);

    if (items.length === 0) return null;

    return (
        <div
            className="w-full border-b border-white/8 bg-[#03151a]/95 backdrop-blur-md relative overflow-hidden"
            data-testid="live-ticker"
            style={{ boxShadow: "inset 0 -1px 0 rgba(34,224,180,0.15)" }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* left "LIVE" tag */}
            <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center gap-2 pl-4 pr-4 bg-gradient-to-r from-[#03151a] via-[#03151a]/95 to-transparent">
                <span className="relative inline-flex items-center justify-center w-2 h-2">
                    <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-70" />
                    <span className="relative w-2 h-2 rounded-full bg-red-500" />
                </span>
                <span className="text-[10px] font-bold tracking-widest text-red-300 uppercase">
                    LIVE
                </span>
                <Radio className="w-3 h-3 text-red-300 opacity-70" />
            </div>
            {/* right fade */}
            <div className="absolute right-0 top-0 bottom-0 w-16 z-20 bg-gradient-to-l from-[#03151a] to-transparent pointer-events-none" />

            {/* scrolling track */}
            <div
                ref={trackRef}
                className="flex whitespace-nowrap py-2.5 pl-24"
                style={{
                    animation: paused
                        ? "none"
                        : `rc-ticker-scroll ${Math.max(30, items.length * 5)}s linear infinite`,
                    willChange: "transform",
                }}
            >
                {/* Render items twice for seamless loop */}
                {[...items, ...items].map((it, i) => (
                    <TickerItem key={`${it.check_id}-${i}`} item={it} />
                ))}
            </div>

            {/* inline animation keyframes */}
            <style>{`
                @keyframes rc-ticker-scroll {
                    0%   { transform: translate3d(0, 0, 0); }
                    100% { transform: translate3d(-50%, 0, 0); }
                }
            `}</style>
        </div>
    );
}

function TickerItem({ item }) {
    const color = V_COLOR[item.verdict] || "#8890c9";
    const label = V_LABEL[item.verdict] || item.verdict.toUpperCase();
    return (
        <Link
            to={`/check/${item.check_id}`}
            className="inline-flex items-center gap-3 mr-8 group hover:opacity-90"
            data-testid={`ticker-item-${item.check_id}`}
        >
            <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase"
                style={{
                    background: `${color}22`,
                    color,
                    border: `1px solid ${color}55`,
                }}
            >
                <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: color, boxShadow: `0 0 6px ${color}` }}
                />
                {label}
            </span>
            <span className="text-sm text-accent/90 group-hover:text-accent transition-colors font-medium">
                {(item.claim || "").length > 90
                    ? (item.claim || "").slice(0, 88) + "…"
                    : item.claim}
            </span>
            <span className="text-[11px] text-muted-foreground/70 font-mono">
                · {ago(item.created_at)}
            </span>
            <span className="mx-4 text-muted-foreground/25">•</span>
        </Link>
    );
}
